import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

export function VideoModal({ video, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <AnimatePresence>
      {video && (
        <motion.div
          data-testid="video-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-brayo-charcoal/95 p-4 md:p-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="w-full max-w-5xl"
            initial={{ scale: 0.96, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.96, y: 20 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-white/10 py-3">
              <p className="meta-label text-brayo-blue">{video.title}</p>
              <button data-testid="video-modal-close" onClick={onClose} aria-label="Close video" className="meta-label min-h-[44px] border border-white/20 px-5 py-3 text-white transition-colors hover:border-brayo-blue hover:text-brayo-blue">
                Close ✕
              </button>
            </div>
            <div className="aspect-video w-full bg-black">
              <iframe
                title={video.title}
                src={
                  video.vimeo_id
                    ? `https://player.vimeo.com/video/${video.vimeo_id}?autoplay=1`
                    : `https://www.youtube-nocookie.com/embed/${video.youtube_id}?autoplay=1&rel=0`
                }
                className="h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function StreamLinks({ release, testPrefix = 'release' }) {
  const links = [
    { label: 'Spotify', url: release.spotify_url, testid: `${testPrefix}-spotify-link` },
    { label: 'Apple Music', url: release.apple_url, testid: `${testPrefix}-apple-link` },
    { label: 'YouTube', url: release.youtube_url, testid: `${testPrefix}-youtube-link` },
    { label: 'SoundCloud', url: release.soundcloud_url, testid: `${testPrefix}-soundcloud-link` },
  ].filter((l) => l.url);

  return (
    <div className="flex flex-wrap gap-3">
      {links.map((l, i) => (
        <a
          key={l.label}
          data-testid={l.testid}
          href={l.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`meta-label arrow-slide flex items-center gap-2 px-5 py-3 transition-colors ${
            i === 0
              ? 'bg-brayo-blue text-white hover:bg-brayo-charcoal'
              : 'border border-brayo-line text-brayo-charcoal hover:border-brayo-blue hover:text-brayo-blue'
          }`}
        >
          {l.label}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M7 17L17 7M9 7h8v8" />
          </svg>
        </a>
      ))}
    </div>
  );
}

export default VideoModal;